import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import reinventing from "~/assets/illustrations/whofor/reinventing.png";

import { useDrawOnView } from "../CuadernoVivo/useDrawOnView";
import { WashiTape } from "../CuadernoVivo/WashiTape";

/**
 * El puente hero→curso: card SUPERPUESTA (no una sección).
 *
 * Concepto: la línea de voz del manual («no decimos "potenciá tu futuro", decimos "lo
 * que te lleva 40 minutos, en 5"») hecha objeto — acá, en horas contra minutos. El
 * tiempo viejo aparece TACHADO con un trazo de lápiz rojo que se dibuja al entrar en
 * viewport —la corrección de cuaderno, el gesto más propio de la marca— y el nuevo queda
 * en rojo. Es una ilustración del tipo de ganancia, no una estadística: por eso no lleva
 * fuente y no hay que agregarle números.
 *
 * ── El solapamiento, que es la parte delicada ────────────────────────────────────────
 * La card tiene que asomar MEDIA altura sobre el fold: entrás a la página y ves la mitad,
 * cortada, invitando a scrollear. Eso NO se puede hacer con un `-mt` fijo, porque el alto
 * del hero es `100vh` y el de la card depende de cuánto ocupe su texto.
 *
 * Y tiene un techo: la card jamás puede tapar la nota manuscrita "ya lo hace gente como
 * vos" que cuelga de la ilustración del hero. Ese es el límite duro que fija el diseño.
 *
 * Entonces el offset se MIDE en el cliente: se calcula cuánto hay que subir para que
 * quede la mitad afuera, y se recorta contra el borde inferior de la nota (que se busca
 * por `[data-hero-note]`). Gana el más chico de los dos. En SSR y antes de medir vale el
 * valor conservador (el de la nota), así que nunca hay un frame donde la tape.
 */
/** Elementos del hero que la card no puede tapar, del más restrictivo al menos. */
const CEILING_SELECTORS = ["[data-hero-note]", "[data-hero-floor]"];

/**
 * El techo VISIBLE. Ojo con esto: `data-hero-note` existe en el DOM también en mobile
 * (está oculto por CSS, no desmontado), así que elegir "el primero que exista" agarraba
 * un elemento de altura 0 y terminaba descartando el límite — la card se subía libre y
 * le tapaba la metadata al hero. Hay que elegir por visibilidad, no por existencia.
 */
function visibleCeiling(): HTMLElement | null {
  for (const sel of CEILING_SELECTORS) {
    const el = document.querySelector<HTMLElement>(sel);
    if (el && el.getBoundingClientRect().height > 0) return el;
  }
  return null;
}

export function EscalonCero() {
  const { t } = useTranslation();
  const drawRef = useDrawOnView<HTMLDivElement>(0.4);
  const cardRef = useRef<HTMLDivElement>(null);
  const [pull, setPull] = useState<number | null>(null);
  // Cuánto pull hay YA aplicado en el DOM. Sin esto la medición se muerde la cola:
  // se mide la posición que el propio margen produjo, se recalcula sobre ella, y el
  // valor oscila entre dos puntos en vez de converger.
  const appliedRef = useRef(0);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const measure = () => {
      const rect = card.getBoundingClientRect();
      const cardHeight = rect.height;
      // Posición que la card tendría SIN pull: la actual más lo ya aplicado. Esto es
      // lo que hace idempotente al cálculo — mida cuando mida, da el mismo resultado.
      const naturalTop = rect.top + window.scrollY + appliedRef.current;

      // 1) Cuánto subir para que la MITAD de la card quede sobre el fold. Vale también
      //    en mobile: es el canal por el que entra la mayoría desde publicidad, y ver
      //    la card cortada es lo que dice "esto sigue abajo".
      const wantedPull = naturalTop - (window.innerHeight - cardHeight / 2);

      // 2) Techo. En desktop, el borde inferior de la nota manuscrita del hero; la card
      //    puede llegar hasta ahí más un respiro, nunca por encima. En mobile la nota
      //    está oculta, así que el piso lo marca la metadata del hero.
      const ceiling = visibleCeiling();
      let maxPull = wantedPull;
      if (ceiling) {
        const ceilBottomAbs = ceiling.getBoundingClientRect().bottom + window.scrollY;
        maxPull = naturalTop - (ceilBottomAbs + 28);
      }

      const next = Math.max(0, Math.min(wantedPull, maxPull));
      appliedRef.current = next;
      setPull(next);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(card);
    // También hay que vigilar los TECHOS, no sólo la card: cuando la metadata del hero
    // pasa de una línea a dos (mobile, carga de fuentes), su borde inferior baja y el
    // límite cambia. Observando sólo la card, la card le quedaba montada encima. Se
    // observan LOS DOS candidatos porque cuál manda depende del breakpoint.
    for (const sel of CEILING_SELECTORS) {
      const el = document.querySelector<HTMLElement>(sel);
      if (el) ro.observe(el);
    }
    window.addEventListener("resize", measure);
    // Las fuentes reflowean el texto y cambian el alto de la card.
    document.fonts?.ready.then(measure).catch(() => {});
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // La puntuación que sigue al tachado (", con…") tiene que quedar PEGADA a él: el
  // navegador puede cortar línea justo después del inline-block y dejaba la coma
  // huérfana abriendo el renglón siguiente.
  const mid = t("landing.launch.step0.mid");
  const midGlue = (mid.match(/^\S*/) ?? [""])[0];
  const midRest = mid.slice(midGlue.length);

  return (
    // El margen arranca en CERO por clase y lo maneja enteramente el estilo inline
    // medido. Si Tailwind dejara un `-mt` acá, el cálculo tendría que adivinar ese
    // valor: así el punto de partida es conocido, y en SSR (antes de medir) la card
    // nunca asoma sobre la nota del hero.
    <div
      className="relative mb-10 px-5 sm:px-8 md:mb-16"
      style={pull === null ? undefined : { marginTop: `-${pull}px` }}
    >
      <div ref={cardRef} className="relative z-10 mx-auto max-w-[820px]">
        <div
          ref={drawRef}
          className="landing-draw relative grid items-center gap-5 rounded-[10px] border border-landing-border bg-landing-paper px-6 py-7 shadow-landing-xl sm:gap-8 sm:px-9 sm:py-8 md:grid-cols-[150px_1fr]"
        >
          {/* La ilustración va PRIMERA en el DOM: a la izquierda en desktop y arriba del
              texto en mobile, sin necesidad de `order`. */}
          <figure className="relative m-0 max-md:mx-auto max-md:w-[140px]">
            <WashiTape variant={3} />
            <img
              src={reinventing}
              alt=""
              loading="lazy"
              width={150}
              height={150}
              className="landing-media-paper aspect-square w-full rounded-[10px] object-cover shadow-landing-sm"
            />
          </figure>

          <div>
            <p className="font-display text-balance text-[clamp(22px,3vw,32px)] font-extrabold leading-[1.25] tracking-tight text-landing-text">
              {t("landing.launch.step0.pre")}
              <span className="whitespace-nowrap">
                <span className="relative inline-block">
                  {t("landing.launch.step0.strike")}
                  {/* El tachón. pathLength=1 + .landing-draw: se dibuja solo y respeta
                      reduced-motion (queda dibujado de entrada). */}
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 100 12"
                    preserveAspectRatio="none"
                    className="pointer-events-none absolute left-[-3%] top-[54%] h-[12px] w-[106%] -translate-y-1/2 overflow-visible"
                  >
                    <path
                      pathLength="1"
                      d="M2,8 C 30,4 70,9 98,4"
                      className="fill-none stroke-landing-red"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      style={{ animationDelay: "0.35s" }}
                    />
                  </svg>
                </span>
                {midGlue}
              </span>
              {midRest}
              <em className="not-italic text-landing-red">{t("landing.launch.step0.em")}</em>
              {t("landing.launch.step0.post")}
            </p>
            <p className="mt-3 max-w-[52ch] text-pretty text-[15px] leading-relaxed text-landing-muted">
              {t("landing.launch.step0.support")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
